import type { BN } from "@coral-xyz/anchor";
import type { MintInfo } from "@solana/spl-token";
import type { PublicKey } from "@solana/web3.js";
import { useState, useEffect, useMemo } from "react";

import type { DepositWithMintAccount } from "../../services/governance/accounts";
import type {
  DepositRecordUI,
  DepositType,
} from "../../tw/components/molecules/DepositCard/types";
import { lamportsBNToNumber } from "@utils/lamports-to-number";
import { useWalletInfo } from "contexts/WalletInfoContext";
import { coinSymbols } from "services/domain/coinSymbols";

import { useDeposits } from "./useDeposits";
import { useRealmByPubkeyQuery } from "./useRealmByPubkeyQuery";

const unlockedTypes = ["none"];

interface DepositBox {
  mintPk: PublicKey;
  mint: MintInfo;
  currentAmount: BN;
  lockUpKind: string;
}

function getDepositRecordType(
  deposit: DepositWithMintAccount
): DepositType | undefined {
  const lockupType = Object.keys(deposit.lockup.kind)[0];
  const lockupEndTs = deposit.lockup.endTs
    ? deposit.lockup.endTs.toNumber() * 1000
    : Date.now();

  const isActive = lockupType === "constant";

  const isClaimable =
    lockupType === "cliff" && lockupEndTs && lockupEndTs < Date.now();

  if (isActive) {
    return "active";
  }

  if (isClaimable) {
    return "claim";
  }

  return "unstake";
}

/**
 * Split vested deposit record into multiply deposits records based on available tokens
 * @param deposit
 */
function splitVestedDeposit(deposit: DepositWithMintAccount) {
  const records: DepositRecordUI[] = [];
  if (deposit.currentlyLocked.gtn(0)) {
    records.push({
      ...deposit,
      type: "vested",
      amount: deposit.currentlyLocked,
    });
  }

  if (deposit.available.gtn(0)) {
    records.push({ ...deposit, type: "claim", amount: deposit.available });
  }

  return records;
}

export const useParticipateInGovernance = () => {
  const { tokenAccounts } = useWalletInfo();
  const { data: deposits, isLoading } = useDeposits();
  const { data: realm } = useRealmByPubkeyQuery();
  const [depositedMnde, setDepositedMnde] = useState<number>(0);
  const [totalLockedMnde, setTotalLockedMnde] = useState<number>(0);

  const isNextSameRecord = (x: DepositBox, next: DepositWithMintAccount) => {
    const nextType = Object.keys(next.lockup.kind)[0];
    return (
      x.mintPk.toBase58() === next.mint.publicKey.toBase58() &&
      ((!unlockedTypes.includes(x.lockUpKind) &&
        !unlockedTypes.includes(nextType)) ||
        (unlockedTypes.includes(x.lockUpKind) &&
          unlockedTypes.includes(nextType)))
    );
  };

  const reducedDeposits = useMemo(() => {
    return deposits?.reduce((curr, next) => {
      const nextType = Object.keys(next.lockup.kind)[0];
      const isUnlockedType = unlockedTypes.includes(nextType);
      const currentValue = curr.find((x) => {
        return isNextSameRecord(x, next);
      });
      if (typeof currentValue === "undefined") {
        curr.push({
          mintPk: next.mint.publicKey,
          mint: next.mint.account,
          currentAmount: isUnlockedType ? next.available : next.currentlyLocked,
          lockUpKind: nextType,
        });
      } else {
        curr.forEach((x) => {
          if (isNextSameRecord(x, next)) {
            // eslint-disable-next-line no-param-reassign
            x.currentAmount = x.currentAmount.add(
              unlockedTypes.includes(x.lockUpKind)
                ? next.available
                : next.currentlyLocked
            );
          }
        });
      }
      return curr;
    }, [] as DepositBox[]);
  }, [deposits]);

  const formattedDeposits: DepositRecordUI[] = useMemo(() => {
    return (deposits || [])
      .filter((deposit) => Object.keys(deposit.lockup.kind)[0] !== "none")
      .map((next) => {
        const type = getDepositRecordType(next);
        const amount = type === "claim" ? next.available : next.currentlyLocked;
        return {
          ...next,
          type: getDepositRecordType(next),
          amount,
        } as DepositRecordUI;
      })
      .reduce((sum, deposit) => {
        const lockupType = Object.keys(deposit.lockup.kind)[0];
        if (lockupType === "monthly" || lockupType === "daily") {
          const splited = splitVestedDeposit(deposit);
          return sum.concat(splited);
        }
        sum.push(deposit);
        return sum;
      }, [] as DepositRecordUI[]);
  }, [deposits]);

  const activePositions = useMemo(() => {
    return formattedDeposits?.filter((depo) => depo.type === "active");
  }, [formattedDeposits]);

  const lockUpNoneDepositedMnde = reducedDeposits?.find(
    (item) => item.lockUpKind === "none"
  );

  const lockedMnde = reducedDeposits?.find(
    (item) => item.lockUpKind !== "none"
  );

  useEffect(() => {
    if (lockUpNoneDepositedMnde) {
      setDepositedMnde(
        lamportsBNToNumber(
          lockUpNoneDepositedMnde.currentAmount,
          lockUpNoneDepositedMnde.mint.decimals
        )
      );
    }

    if (lockedMnde) {
      setTotalLockedMnde(
        lamportsBNToNumber(lockedMnde.currentAmount, lockedMnde.mint.decimals)
      );
    }
  }, [lockUpNoneDepositedMnde, lockedMnde]);

  const totalAvailableMndeAmount =
    Number(tokenAccounts.get(coinSymbols.MNDE)?.balance ?? 0) +
    Number(depositedMnde);

  const unstackingPositions = useMemo(() => {
    return formattedDeposits?.filter(
      (depo) =>
        depo.type === "unstake" ||
        depo.type === "claim" ||
        depo.type === "vested"
    );
  }, [formattedDeposits]);

  const filteredUnstackingPosition = unstackingPositions?.filter(
    (x) =>
      x.index !==
      unstackingPositions.find(
        (depo) =>
          typeof depo.lockup.kind.none !== "undefined" &&
          depo.mint.publicKey.toBase58() ===
            realm?.account.communityMint.toBase58() &&
          depo.isUsed &&
          !depo.allowClawback
      )?.index
  );

  const percentAvailable = useMemo(() => {
    const total = totalLockedMnde + totalAvailableMndeAmount;
    return (totalAvailableMndeAmount / total) * 100;
  }, [totalLockedMnde, totalAvailableMndeAmount]);

  const percentStaked = useMemo(
    () => 100 - percentAvailable,
    [percentAvailable]
  );

  return {
    totalAvailableMndeAmount,
    totalLockedMnde,
    isLoading,
    activePositions,
    percentAvailable,
    percentStaked,
    unstackingPositions: filteredUnstackingPosition,
  };
};
